import { useMemo, useState } from "react";
import { Modal, Button, Table } from "react-bootstrap";
import PageLoading from "components/PageLoading";
import { FaCheckCircle, FaDownload, FaSpinner } from "react-icons/fa";
import { sortHistoricoDocumentacaoDesc } from "utils/date";
import type { PaymentHistoryEntry } from "../shared/paymentListTypes";
import { downloadPagamentoArquivoEvento } from "../../../services/pagamentoAvaliacaoService";
import "../../Documentation/Shipping/VersionSelector/components/ShippingHistory/ShippingHistory.css";
import "./HistoryPagamento.css";

export interface HistoryPagamentoProps {
  show: boolean;
  onHide: () => void;
  historyData: PaymentHistoryEntry[];
  loading?: boolean;
}

const HistoryPagamento = ({
  show,
  onHide,
  historyData = [],
  loading = false,
}: HistoryPagamentoProps) => {
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const historyOrdenado = useMemo(
    () => sortHistoricoDocumentacaoDesc(historyData),
    [historyData],
  );

  const hasArquivoColumn = useMemo(
    () => historyOrdenado.some((item) => item?.arquivoId != null),
    [historyOrdenado],
  );

  const handleDownload = async (item: PaymentHistoryEntry) => {
    const arquivoId = Number(item?.arquivoId);
    if (!arquivoId) return;

    try {
      setDownloadingId(arquivoId);
      await downloadPagamentoArquivoEvento(
        arquivoId,
        `pagamento_evento_${arquivoId}.pdf`,
      );
    } catch (err) {
      console.error("Erro ao baixar arquivo do histórico de pagamento:", err);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered className="doc-history-modal">
      <Modal.Header closeButton>
        <Modal.Title className="modal-title">HISTÓRICO DO PAGAMENTO</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="doc-history-modal-scroll-pane doc-history-modal-scroll-pane--center">
            <PageLoading minHeight="200px" message="Carregando histórico..." />
          </div>
        ) : historyOrdenado.length === 0 ? (
          <div className="doc-history-modal-scroll-pane doc-history-modal-scroll-pane--center text-center py-3">
            <p className="mb-0">Nenhum histórico encontrado.</p>
          </div>
        ) : (
          <div className="doc-history-modal-scroll-pane">
            <Table bordered className="doc-history-table mb-0">
              <thead>
                <tr>
                  <th>AÇÃO</th>
                  <th>DATA/HORA</th>
                  {hasArquivoColumn ? <th>ARQUIVO</th> : null}
                </tr>
              </thead>
              <tbody>
                {historyOrdenado.map((item, index) => {
                  const isDownloading = downloadingId === item?.arquivoId;
                  return (
                    <tr
                      key={
                        item.id != null
                          ? `h-${item.id}`
                          : `${item.acao}-${item.data}-${index}`
                      }
                    >
                      <td>
                        <FaCheckCircle className="icon-check" /> {item.acao}
                      </td>
                      <td>{item.data}</td>
                      {hasArquivoColumn ? (
                        <td className="pagamento-history-arquivo-cell">
                          {item?.arquivoId != null ? (
                            <button
                              type="button"
                              className="btn btn-link p-0 pagamento-history-download"
                              title="Baixar arquivo"
                              disabled={isDownloading}
                              onClick={() => handleDownload(item)}
                            >
                              {isDownloading ? (
                                <FaSpinner className="pagamento-history-spin" />
                              ) : (
                                <FaDownload />
                              )}{" "}
                              Baixar
                            </button>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}

        <div className="doc-history-modal-footer text-center">
          <Button className="btn-ok" onClick={onHide}>
            OK
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default HistoryPagamento;
